
import { useForm, FormProvider } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProductSelect } from "./components/ProductSelect";
import { PlanTypeSelect } from "./components/PlanTypeSelect";
import { PriceInput } from "./components/PriceInput";
import { DurationInput } from "./components/DurationInput";
import { TypeSelect } from "./components/TypeSelect";
import { StatusSelect } from "./components/StatusSelect";
import type { PlanFormProps, PlanFormValues } from "./types";

export const PlanForm = ({ initialValues, mode = 'create', onSuccess }: PlanFormProps) => {
  const { toast } = useToast();

  const methods = useForm<PlanFormValues>({
    defaultValues: initialValues || {
      feature_id: '',
      name: 'Monthly',
      price: 0,
      duration: 30,
      type: 'subscription',
      status: 'active',
    },
  });

  const onSubmit = async (data: PlanFormValues) => {
    try {
      // Set price to 0 for Free Trial plans
      const finalData = {
        ...data,
        price: data.name === 'Free Trial' ? 0 : data.price,
        duration: data.name === 'Free Trial' ? 30 : data.duration // Default trial period to 30 days
      };

      if (mode === 'create') {
        const { error } = await supabase
          .from('plans')
          .insert([finalData]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Plan created successfully",
        });
      } else {
        const { error } = await supabase
          .from('plans')
          .update(finalData)
          .eq('id', initialValues?.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Plan updated successfully",
        });
      }
      
      methods.reset();
      onSuccess();
    } catch (error) {
      console.error("Error saving plan:", error);
      toast({
        title: "Error",
        description: `Failed to ${mode} plan`,
        variant: "destructive",
      });
    }
  };

  // Watch the plan name to disable price input for Free Trial
  const planName = methods.watch('name');
  
  return (
    <FormProvider {...methods}>
      <Form {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
          <ProductSelect />
          <PlanTypeSelect />
          <PriceInput disabled={planName === 'Free Trial'} />
          <DurationInput defaultValue={planName === 'Free Trial' ? 30 : undefined} />
          <TypeSelect />
          <StatusSelect />
          <Button type="submit" className="w-full">
            {mode === 'create' ? 'Create Plan' : 'Update Plan'}
          </Button>
        </form>
      </Form>
    </FormProvider>
  );
};
